import {
    DICE_RESOLVABLE_SOURCE,
    DeviceType,
    EndpointVersions,
    createAdsMacroHeaderMiddleware,
    createDiceSourceResolver,
    isChromecastSessionV1,
    isChromecastSessionV2
} from '@dicetechnology/dice-doris';
import type { AdvertisingOptions, ChromecastSession, DiceResolvableSourceCustomData, StreamSelector } from '@dicetechnology/dice-doris';
import { AuthManager, UserTokenManager } from '@dicetechnology/dice-doris/chromecast';
import type { BeaconPayloadV1 } from '@dicetechnology/dice-doris/dist/types/dice/types/BeaconPayloadV1';
import { Http } from '@dicetechnology/dice-unity/lib/services/Http/index';
import { tokenRefresh } from '@dicetechnology/dice-unity/lib/services/Http/middlewares/refresh';
import { Translation } from '@dicetechnology/dice-unity/lib/services/i18n/Translation';
import { Logger } from '@dicetechnology/dice-unity/lib/services/Logger/index';
import type { ILoggerMessage as LoggerMessage } from '@dicetechnology/dice-unity/lib/services/Logger/types';
import { DEFAULT_THEME } from '@dicetechnology/dice-unity/lib/services/realm/constants';
import { Realm } from '@dicetechnology/dice-unity/lib/services/realm/Realm';
import { AvailabilityState } from '@dicetechnology/dice-unity/lib/services/realm/types';
import type { IStorageProvider as StorageProvider } from '@dicetechnology/dice-unity/lib/services/Storage/types';
import { DisplayMode } from '@dicetechnology/dice-unity/lib/types/common';
import { LogLevels, LogService } from '@dicetechnology/doris-core';
import type { SourceResolver, SourceResolverDefinition } from '@dicetechnology/doris-core';
import { getChromecastModel } from '@dicetechnology/doris-plugin-chromecast-receiver';
import autoBind from 'auto-bind';
import EventEmitter from 'eventemitter3';
import jwtDecode from 'jwt-decode';

import pkg from '../../../package.json';
import { API_KEY, DEFAULT_SETTINGS, LOGGER_OPTIONS, NO_REALM_DATA_ERROR } from '../../constants';
import { Environment, ErrorCodes, Labels, StorageKeys } from '../../enums';
import { DiceReceiverError } from '../../errors';
import type { Settings } from '../../types';
import {
    getMediaCapabilitiesSourceResolverConfig,
    localeIsRightToLeft,
    preferDashStreams,
    removeUndefinedProps,
    validateBeaconPayload
} from '../../utils';
import { Beacon } from '../Beacon';
import { CustomMessageListener } from '../CustomMessageListener';
import { GooglePalManager } from '../GooglePalManager';
import { LocalStorageManager } from '../LocalStorageManager';
import { PlaylistManager } from '../PlaylistManager';

import { DiceReceiverManagerEvents } from './enums';
import type { AuthToken, DiceReceiverManagerEventMap, UserDetails } from './types';
import { getApiBaseUrl, getEnvironment, inferEnvironmentFromBaseUrl } from './utils';

interface Services {
    authManager: AuthManager;
    customMessageListener: CustomMessageListener;
    httpService: Http;
    logger: Logger;
    playlistManager: PlaylistManager;
    realmService: Realm;
    resolveSource: SourceResolver;
    storageProvider: StorageProvider;
    translationService: Translation;
    userTokenManager: UserTokenManager;
}

export class DiceReceiverManager extends EventEmitter<DiceReceiverManagerEventMap> {
    private static _instance?: DiceReceiverManager;
    private _beacon?: Beacon;
    private _environment: Environment;
    private _googlePalManager?: GooglePalManager;
    private _realm: string;
    private _services: Services;
    private _settings?: Settings;
    private _settingsLoaded: Promise<void>;

    private constructor() {
        super();
        autoBind(this);

        const urlSearchParams = new URLSearchParams(window.location.search);
        const realm = urlSearchParams.get('realm');

        if (!realm) {
            throw NO_REALM_DATA_ERROR;
        }

        this._realm = realm;
        this._environment = Environment.PROD;

        this._services = this.createServices(realm, this._environment);

        // Start fetching settings after `Promise.resolve()` to avoid infinite loops
        // if any of the Http middlewares rely on the `DiceReceiverManager`.
        this._settingsLoaded = Promise.resolve()
            .then(() => this.fetchSettings())
            .catch(() => {
                this.emit(DiceReceiverManagerEvents.ERROR, NO_REALM_DATA_ERROR);
            });

        window.diceReceiverManager = this;
    }

    public static getInstance(): DiceReceiverManager {
        return (this._instance ??= new DiceReceiverManager());
    }

    /**
     * Configures all services for a given realm and environment.
     */
    public async configure(session: ChromecastSession, beaconPayload: BeaconPayloadV1): Promise<void> {
        this.assertChromecastSession(session);

        // Stop previous beacon if it exists
        this._beacon?.stop(true);

        // Configure realm + environment
        const realm = session.realm;
        const environment = isChromecastSessionV2(session)
            ? getEnvironment(session.env) ?? Environment.PROD
            : inferEnvironmentFromBaseUrl(session.baseUrl);

        const realmSwitchRequired = this._realm !== realm;
        const environmentSwitchRequired = this._environment !== environment;

        if (realmSwitchRequired || environmentSwitchRequired) {
            this._settingsLoaded = this.switchRealm(realm, environment);
        }

        await this._settingsLoaded;

        await this.getAuthManager().loginWithSession(session);

        // Generate beacon
        this._beacon = this.createBeaconFromSession(beaconPayload);

        this.emit(DiceReceiverManagerEvents.CONFIGURED);
    }

    public getAuthManager(): AuthManager {
        return this._services.authManager;
    }

    public getBeacon(): Beacon | undefined {
        return this._beacon;
    }

    public getCustomMessageListener(): CustomMessageListener {
        return this._services.customMessageListener;
    }

    public getGooglePalManager(): GooglePalManager | undefined {
        return this._googlePalManager;
    }

    public getHttpService(): Http {
        return this._services.httpService;
    }

    public getLogger(): Logger {
        return this._services.logger;
    }

    public getPlaylistManager(): PlaylistManager {
        return this._services.playlistManager;
    }

    public getRealm(): string {
        return this._realm;
    }

    public getSettings(): Settings | undefined {
        return this._settings;
    }

    public getSourceResolverDefinition(): SourceResolverDefinition {
        // Create a proxy to resolveSource, so we can swap out the source resolver
        // code when a realm is switched, and a new Http service is created.
        return {
            resolutionType: DICE_RESOLVABLE_SOURCE,
            resolveSource: (id, customData, context) => {
                const data: DiceResolvableSourceCustomData = {
                    ...customData
                };
                if (this.getWillUseV5Endpoint(data.isLive)) {
                    data.experimentalEndpointVersion = EndpointVersions.V5;
                } else {
                    data.experimentalEndpointVersion = EndpointVersions.V4;
                }
                return this._services.resolveSource(id, data, context);
            }
        };
    }

    public getStorageProvider(): StorageProvider {
        return this._services.storageProvider;
    }

    public getTranslationFor(label: string): string | undefined {
        return this._services.translationService.get(label) ?? undefined;
    }

    public async getUserDetails(): Promise<UserDetails> {
        const authToken = await this.getStorageProvider().get(StorageKeys.AUTH_TOKEN);
        if (!authToken) {
            return {};
        }

        const { ads, ip, sub, lo2, loc } = jwtDecode<AuthToken>(authToken);

        return {
            ip,
            userId: sub,
            location: lo2 ?? loc,
            advertisingId: ads?.id
        };
    }

    public getWillUseV5Endpoint(isLive = false): boolean {
        return (isLive && this._settings?.liveEndpoint === 'V5') || (!isLive && this._settings?.videoEndpoint === 'V5');
    }

    public async sendLog(message: LoggerMessage): Promise<void> {
        const { userId, location, ip } = await this.getUserDetails();
        this.getLogger().log({ ...message, userId, location, ip });
    }

    private assertChromecastSession(session: ChromecastSession): void {
        // We don't have props from either methods of authentication
        if (!('authorisationToken' in session) && !('exid' in session)) {
            const title = this.getTranslationFor(Labels.AUTHENTICATION_ERROR) ?? 'AUTHENTICATION ERROR';
            const message = this.getTranslationFor(Labels.ERROR_ACCESSING_VIDEO) ?? 'ERROR ACCESSING VIDEO';
            this.emit(
                DiceReceiverManagerEvents.ERROR,
                new DiceReceiverError(title, message, ErrorCodes.NO_AUTH_TOKEN, 'INVALID_AUTH_TOKEN')
            );
        }

        if (isChromecastSessionV1(session)) {
            const { beacon: beaconPayload } = session;

            if (!session.refreshToken) {
                console.error('refreshToken not found in session');
            }

            try {
                validateBeaconPayload(beaconPayload);
            } catch (error) {
                // We should always end up inside the if block
                if (error instanceof DiceReceiverError) {
                    this.emit(DiceReceiverManagerEvents.ERROR, error);
                }
                console.error('Error', error);
            }
        }
    }

    private createBeaconFromSession(beaconPayload: BeaconPayloadV1): Beacon {
        const httpService = this.getHttpService();
        if (!httpService) {
            throw new Error('HttpService is not initialized!');
        }

        const { action, endpoint, video, progress } = beaconPayload;

        const beacon = new Beacon(httpService, endpoint, ({ code, message: messageCode, title: titleCode }) => {
            const title = this.getTranslationFor(titleCode) ?? titleCode;
            const message = this.getTranslationFor(messageCode) ?? messageCode;
            this.emit(DiceReceiverManagerEvents.ERROR, new DiceReceiverError(title, message, code, 'BEACON_ERROR'));
        });
        beacon.configure(video, progress || 0, action);

        return beacon;
    }

    private createServices(realm: string, environment: Environment): Services {
        const logger = new Logger({
            ...LOGGER_OPTIONS,
            realm
        });
        const localLogService = new LogService({
            console: console,
            logLevel: LogLevels.DEBUG,
            writeToConsole: true
        });

        const storageProvider = new LocalStorageManager(realm, environment);
        storageProvider.set(StorageKeys.REALM, realm);
        storageProvider.set(StorageKeys.API_KEY, API_KEY);

        const httpService = new Http({
            baseUrl: getApiBaseUrl(environment),
            storageProvider,
            useDefaultErrorMiddleware: false
        });
        const adsMacroHeaderMiddleware = createAdsMacroHeaderMiddleware({
            playerType: 'Chromecast',
            playerVersion: pkg.version,
            deviceType: DeviceType.CONNECTED_DEVICE,
            getIsAdsEnabled: async () => this._settings?.enableAds ?? false,
            getIsGooglePalEnabled: async () => this._googlePalManager?.getIsEnabled() ?? false,
            createGooglePalNonces: (contextId) => this._googlePalManager?.createGooglePalNonces(contextId),
            additionalAdHeaders: {
                ['CM-DVC-MODEL']: getChromecastModel()
            }
        });
        httpService.applyRequestMiddleware([adsMacroHeaderMiddleware]);

        const userTokenManager = new UserTokenManager({
            storageProvider
        });

        const authManager = new AuthManager({
            httpService,
            userTokenManager,
            logService: localLogService
        });
        httpService.applyErrorMiddleware([
            tokenRefresh((options) =>
                httpService.refresh(options).then(
                    (response) => authManager.persistTokensFromTokenRefresh(response),
                    () => authManager.refreshSession()
                )
            )
        ]);

        const resolveSource = this.createSourceResolver(httpService);

        const realmService = new Realm({
            defaultTheme: DEFAULT_THEME,
            http: httpService,
            name: realm,
            storageProvider,
            shouldBypassStorageService: true
        });

        const translationService = new Translation({
            http: httpService,
            storageProvider,
            shouldBypassStorageService: true
        });

        const playlistManager = new PlaylistManager(httpService, localLogService);

        const customMessageListener = new CustomMessageListener({
            playlistManager
        });

        return {
            authManager,
            httpService,
            logger,
            playlistManager,
            realmService,
            resolveSource,
            storageProvider,
            translationService,
            userTokenManager,
            customMessageListener
        };
    }

    private createSourceResolver(httpService: Http, advertising?: AdvertisingOptions): SourceResolver {
        const mediaCapabilities = getMediaCapabilitiesSourceResolverConfig();
        const defaultStreamSelector: StreamSelector = (supportedStreams) => {
            if (this._settings?.preferDash) {
                supportedStreams.sort(preferDashStreams);
            }
            return supportedStreams[0];
        };

        const { resolveSource } = createDiceSourceResolver({
            advertising,
            httpService,
            mediaCapabilities,
            defaultStreamSelector
        });
        return resolveSource;
    }

    private async fetchSettings(): Promise<void> {
        const realmService = this._services.realmService;
        const fetchRealmData = realmService.fetch();
        const fetchTranslations = this._services.translationService.fetch();

        const [realmData] = await Promise.all([fetchRealmData, fetchTranslations]);

        this._settings = {
            ...DEFAULT_SETTINGS,
            ...removeUndefinedProps({
                backgroundColor: realmData.appSettings?.chromecast.backgroundColor,
                backgroundImageUrl: realmData.theme.images.chromecastBackgroundImage,
                convivaCustomerKey: realmData.settings.convivaCustomerKey,
                datazoomConfigId: realmData.settings.datazoomConfigId,
                logoImageUrl: realmData.theme.images.chromecastLogo,
                logoMiniImageUrl: realmData.theme.images.chromecastLogoMini,
                muxEnvironmentKey: realmData.settings.videoAnalyticsKey,
                progressColor: realmData.appSettings?.chromecast.progressColor,
                seekBarDuringAdBreak: realmData.settings.seekBarDuringAdBreak,
                seekBarHexColor: realmData.settings.seekBarHexColor,
                showContentTitles: realmData.settings.contentTitles === DisplayMode.SHOW,
                subtitlePolicy: realmData.settings.audioSubtitleLanguages?.audioToSubtitleLanguages,
                textColor: realmData.appSettings?.chromecast.textColor
            } as Partial<Settings>),
            enableAds: realmData.settings.adsConfigurationStatus === AvailabilityState.ENABLED,
            enableGooglePal: realmData.settings.palSdkConfiguration?.enableMode === AvailabilityState.ENABLED,
            enableHideAdUiElements: realmData.settings.adUiElements === DisplayMode.HIDE,
            enableOmSdk: realmData.settings.omSdkEnableMode === AvailabilityState.ENABLED,
            enablePauseAds: realmService.dataRaw?.pauseAds === AvailabilityState.ENABLED,
            enablePauseAdsLoopMode: realmService.dataRaw?.loopPauseAds === AvailabilityState.ENABLED,
            enableWhyThisAdIcon: realmData.settings.palSdkConfiguration?.whyThisAdIcon === DisplayMode.SHOW,
            enhancedMetadataViews: realmData.settings.enhancedMetadataViews === AvailabilityState.ENABLED,
            liveEndpoint: realmData.settings.liveEndpoint,
            persistTextTrackSelection: realmData.settings.persistClosedCaptions === AvailabilityState.ENABLED,
            preferDash: realmService.dataRaw?.settings?.['CHROMECAST_PREFER_DASH'] === 'true',
            treatAllOverlayAdsAsPauseAds: realmService.dataRaw?.treatAllOverlayAdsAsPauseAds === AvailabilityState.ENABLED,
            videoEndpoint: realmData.settings.videoEndpoint
        };

        this.emit(DiceReceiverManagerEvents.SETTINGS_CHANGED, this._settings);

        if (this._settings.enableGooglePal) {
            this._googlePalManager = new GooglePalManager(this._settings.enableOmSdk, realmData.settings.palSdkConfiguration);
            this._services.resolveSource = this.createSourceResolver(this.getHttpService(), {
                adProviders: this._googlePalManager.getAdProviders(),
                googlePalService: this._googlePalManager.getService()
            });
        }

        const selectedLocale = this._services.translationService.selectedLocale;
        if (selectedLocale && localeIsRightToLeft(selectedLocale)) {
            document.body.classList.add('right-to-left');
        } else {
            document.body.classList.remove('right-to-left');
        }

        this.updateCss();
    }

    private switchRealm(realm: string, environment: Environment): Promise<void> {
        this._services = this.createServices(realm, environment);

        if (this._realm !== realm) {
            this._realm = realm;
        }

        if (this._environment !== environment) {
            this._environment = environment;
        }

        return this.fetchSettings();
    }

    private updateCss(): void {
        // Set realm class name on body
        const realmClassName = `realm--${this._realm.replace('.', '-')}`;
        document.body.classList.forEach((className) => {
            if (className.startsWith('realm--') && className !== realmClassName) {
                document.body.classList.remove(className);
            }
        });
        document.body.classList.toggle(realmClassName, true);

        // Update root css variables
        function updateProperty(name: string, value: string | undefined) {
            if (value == null) {
                document.documentElement.style.removeProperty(name);
            } else {
                document.documentElement.style.setProperty(name, value);
            }
        }
        const settings = this.getSettings();
        updateProperty('--background-color', settings?.backgroundColor);
        updateProperty('--background-image-url', settings?.backgroundImageUrl ? `url(${settings.backgroundImageUrl})` : undefined);
        updateProperty('--logo-image-url', settings?.logoImageUrl ? `url(${settings.logoImageUrl})` : undefined);
        updateProperty('--logo-mini-image-url', settings?.logoMiniImageUrl ? `url(${settings.logoMiniImageUrl})` : undefined);
        updateProperty('--progress-color', settings?.progressColor);
        updateProperty('--text-color', settings?.textColor);
        // Update seek bar color only if seekBarHexColor is defined, we have default one in doris-ui package,
        // so we don't want to override it with undefined value
        if (settings?.seekBarHexColor) {
            updateProperty('--ds-ad-color', settings.seekBarHexColor);
        }
    }
}
